// src/cockpit4/components/ContentAssetLibrary.tsx
// ============================================
// P1-功能：内容资产库（媒体工作室）
//
// 功能：
//   1. 标签体系（主题/风格/平台/效果等级）- 自定义标签多维分类
//   2. 智能检索：按效果/主题/类型/时间快速找（搜索 + 多标签筛选 + 平台 + 效果等级 + 排序）
//   3. 素材复用：爆款模板一键套用（复用计数 + 模板摘要复制到剪贴板）
//   4. 内容版本管理：为每个媒体包创建版本快照，可查看历史版本
//   5. 资产统计：总量/各平台分布/质量分布
//   6. 资产详情：完整内容预览 + 标签编辑 + 版本历史 + 复用操作
//
// 数据来源：
//   - RuntimeManager.query(RuntimeName.MediaStudio, { type: "list_packages" })
//     返回 data.packages: MediaPackage[]，每个包含 id/topic/cover/articleScript/video/bgm/status/createdAt
//   - localStorage（key: xeaos_content_asset_tags）持久化自定义标签定义及标签-资产绑定关系
//   - localStorage（key: xeaos_content_asset_favorites）持久化收藏与复用标记
//   - localStorage（key: xeaos_content_asset_versions_<packageId>）持久化每个资产包的版本记录
//   - 无媒体包时显示空状态提示，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FolderArchive,
  Tag,
  Search,
  Star,
  Copy,
  History,
  BarChart3,
  Filter,
  RefreshCw,
  Loader2,
  X,
  Plus,
  Trash2,
  FileText,
  Image as ImageIcon,
  Video as VideoIcon,
  Music,
  CheckCircle2,
  Calendar,
  Save,
  Edit3,
  Layers,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { MediaPackage, MediaAsset } from "../../runtime/MediaStudio/types";

// ============================================
// 常量
// ============================================

const TAGS_STORAGE_KEY = "xeaos_content_asset_tags";
const FAVORITES_STORAGE_KEY = "xeaos_content_asset_favorites";
const VERSIONS_STORAGE_PREFIX = "xeaos_content_asset_versions_";

/** 标签分类 */
type TagCategory = "theme" | "style" | "platform" | "level";

/** 效果等级（由 level 类标签派生） */
type EffectLevel = "viral" | "quality" | "normal" | "needs_work" | "unrated";

/** 排序方式 */
type SortKey = "newest" | "oldest" | "favorite_first" | "reuse_first" | "level";

/** 自定义标签 */
interface CustomTag {
  id: string;
  name: string;
  category: TagCategory;
  color: string;
}

/** 标签存储结构（定义 + 绑定关系） */
interface TagStore {
  tags: CustomTag[];
  /** packageId -> 标签 id 列表 */
  assignments: Record<string, string[]>;
}

/** 收藏/复用记录 */
interface FavoriteRecord {
  packageId: string;
  favoritedAt: string;
  reuseCount: number;
  lastReuseAt?: string;
}

/** 版本快照 */
interface VersionRecord {
  id: string;
  packageId: string;
  createdAt: string;
  note: string;
  topic: string;
  platforms: string[];
  assetCount: number;
  scriptExcerpt: string;
}

const TAG_CATEGORY_LABEL: Record<TagCategory, string> = {
  theme: "主题",
  style: "风格",
  platform: "平台",
  level: "效果等级",
};

const TAG_CATEGORY_COLOR: Record<TagCategory, string> = {
  theme: "#A78BFA",
  style: "#F472B6",
  platform: "#38BDF8",
  level: "#34D399",
};

const PLATFORM_LABEL: Record<string, string> = {
  wechat: "公众号",
  xiaohongshu: "小红书",
  douyin: "抖音",
  bilibili: "B站",
  live: "直播",
};

const PLATFORM_COLOR: Record<string, string> = {
  wechat: "#34D399",
  xiaohongshu: "#FB923C",
  douyin: "#F472B6",
  bilibili: "#A78BFA",
  live: "#38BDF8",
};

const LEVEL_LABEL: Record<EffectLevel, string> = {
  viral: "爆款",
  quality: "优质",
  normal: "普通",
  needs_work: "待优化",
  unrated: "未分级",
};

const LEVEL_COLOR: Record<EffectLevel, string> = {
  viral: "#34D399",
  quality: "#38BDF8",
  normal: "#FBBF24",
  needs_work: "#FB923C",
  unrated: "#6B7280",
};

/** 效果等级优先级（越高越靠前） */
const LEVEL_ORDER: EffectLevel[] = ["viral", "quality", "normal", "needs_work", "unrated"];

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "newest", label: "最近创建" },
  { key: "oldest", label: "最早创建" },
  { key: "favorite_first", label: "收藏优先" },
  { key: "reuse_first", label: "复用最多" },
  { key: "level", label: "效果等级" },
];

/** 内置效果等级标签（首次加载时种子化） */
const DEFAULT_LEVEL_TAGS: CustomTag[] = [
  { id: "lvl_viral", name: "爆款", category: "level", color: "#34D399" },
  { id: "lvl_quality", name: "优质", category: "level", color: "#38BDF8" },
  { id: "lvl_normal", name: "普通", category: "level", color: "#FBBF24" },
  { id: "lvl_needs", name: "待优化", category: "level", color: "#FB923C" },
];

const TAG_COLOR_PALETTE: string[] = [
  "#A78BFA", "#F472B6", "#38BDF8", "#34D399", "#FBBF24",
  "#FB923C", "#60A5FA", "#C084FC", "#2DD4BF", "#F87171",
];

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 检测媒体包包含的平台列表 */
/**
 * v5.2.3 从 Vault 恢复全部产物包（资产库 Vault 兜底，与营销中心产物区一致）
 * 遍历 XEAOS/ContentPackages/{日期}/，按文件名前缀（packageId）解析 frontmatter + 脚本标记
 */
async function fetchPackagesFromVault(): Promise<MediaPackage[]> {
  const result: MediaPackage[] = [];
  try {
    const rootResp = await fetch(`/vault-api/list?dir=${encodeURIComponent('XEAOS/ContentPackages')}&ext=.md`);
    if (!rootResp.ok) return result;
    const rootResult = await rootResp.json();
    if (!rootResult.success) return result;
    const dateDirs = (rootResult.files || []).filter((f: any) => f.isDirectory);
    for (const dir of dateDirs) {
      const subResp = await fetch(`/vault-api/list?dir=${encodeURIComponent(dir.path)}&ext=.md`);
      if (!subResp.ok) continue;
      const subResult = await subResp.json();
      if (!subResult.success) continue;
      for (const file of (subResult.files || [])) {
        const readResp = await fetch(`/vault-api/read?path=${encodeURIComponent(file.path)}`);
        if (!readResp.ok) continue;
        const readResult = await readResp.json();
        const content = String(readResult.content || '');
        const fmMatch = content.match(/^---\n([\s\S]*?)\n---/);
        if (!fmMatch) continue;
        const fm = fmMatch[1];
        const get = (key: string): string => {
          const m = fm.match(new RegExp(`^${key}:\\s*(.+)$`, 'm'));
          return m ? m[1].trim().replace(/^["']|["']$/g, '') : '';
        };
        const id = get('id');
        if (!id) continue;
        const pkg: MediaPackage = {
          id,
          topic: get('topic'),
          status: (get('status') as MediaPackage['status']) || 'completed',
          createdAt: get('createdAt') || new Date().toISOString(),
          completedAt: get('completedAt') || undefined,
          description: get('description') || undefined,
          sourceNotes: [],
        } as MediaPackage;
        const scriptRegex = /<!-- XEAOS_SCRIPT:(\w+) -->\n([\s\S]*?)(?=<!-- XEAOS_SCRIPT:|## 素材链接|$)/g;
        let sm: RegExpExecArray | null;
        const scriptIndex: Record<string, string> = {};
        while ((sm = scriptRegex.exec(content)) !== null) {
          scriptIndex[sm[1]] = sm[2].replace(/\n+$/, '').trim();
        }
        const mk = (purpose: string, text: string): MediaAsset => ({
          id: `asset-${id}-${purpose}`,
          type: 'script' as const,
          purpose: purpose as any,
          content: text,
          title: `${purpose}：${pkg.topic}`,
          model: 'deepseek-chat',
          status: 'completed' as const,
          createdAt: pkg.createdAt,
        });
        if (scriptIndex.article_script) pkg.articleScript = mk('article_script', scriptIndex.article_script);
        if (scriptIndex.video_script) pkg.videoScript = mk('video_script', scriptIndex.video_script);
        if (scriptIndex.xiaohongshu_script) pkg.xiaohongshuScript = mk('xiaohongshu_script', scriptIndex.xiaohongshu_script);
        if (scriptIndex.bilibili_script) pkg.bilibiliScript = mk('bilibili_script', scriptIndex.bilibili_script);
        if (scriptIndex.douyin_script) pkg.douyinScript = mk('douyin_script', scriptIndex.douyin_script);
        result.push(pkg);
      }
    }
  } catch {
    // 忽略，返回空
  }
  return result;
}

function detectPlatforms(pkg: MediaPackage): string[] {
  const platforms: string[] = [];
  if (pkg.wechatCover || pkg.articleScript) platforms.push("wechat");
  if ((pkg.xiaohongshuGrid && pkg.xiaohongshuGrid.length > 0) || pkg.xiaohongshuScript) platforms.push("xiaohongshu");
  if (pkg.douyinCover || pkg.video || pkg.douyinScript) platforms.push("douyin");
  if (pkg.bilibiliScript) platforms.push("bilibili");
  if (pkg.liveStreamScript) platforms.push("live");
  return platforms;
}

/** 获取封面素材（优先级：cover > wechatCover > douyinCover > 九宫格首图） */
function getCoverAsset(pkg: MediaPackage): MediaAsset | undefined {
  return pkg.cover || pkg.wechatCover || pkg.douyinCover || pkg.xiaohongshuGrid?.[0];
}

/** 获取素材的可访问图片 URL */
function assetImageUrl(asset?: MediaAsset): string | undefined {
  if (!asset) return undefined;
  if (asset.url) return asset.url;
  if (asset.b64Json) return `data:image/png;base64,${asset.b64Json}`;
  return undefined;
}

/** 统计媒体包内素材总数 */
function countAssets(pkg: MediaPackage): number {
  let n = 0;
  if (pkg.cover) n++;
  if (pkg.wechatCover) n++;
  if (pkg.xiaohongshuGrid) n += pkg.xiaohongshuGrid.length;
  if (pkg.douyinCover) n++;
  if (pkg.video) n++;
  if (pkg.promoVideo) n++;
  if (pkg.bgm) n++;
  if (pkg.jingle) n++;
  if (pkg.articleScript) n++;
  if (pkg.videoScript) n++;
  if (pkg.xiaohongshuScript) n++;
  if (pkg.bilibiliScript) n++;
  if (pkg.douyinScript) n++;
  if (pkg.liveStreamScript) n++;
  return n;
}

/** 提取脚本摘要（首个可用脚本的前 120 字） */
function getScriptExcerpt(pkg: MediaPackage): string {
  const scripts = [
    pkg.articleScript, pkg.videoScript, pkg.xiaohongshuScript,
    pkg.bilibiliScript, pkg.douyinScript, pkg.liveStreamScript,
  ].filter((s): s is MediaAsset => !!s && !!s.content);
  if (scripts.length === 0) return "";
  const text = scripts[0].content || "";
  return text.length > 120 ? text.slice(0, 120) + "..." : text;
}

/** 根据标签名推断效果等级 */
function levelFromTagName(name: string): EffectLevel | null {
  const n = name.toLowerCase();
  if (n.includes("爆款") || n.includes("viral")) return "viral";
  if (n.includes("优质") || n.includes("quality")) return "quality";
  if (n.includes("普通") || n.includes("normal")) return "normal";
  if (n.includes("待优化") || n.includes("needs")) return "needs_work";
  return null;
}

/** 根据绑定标签派生媒体包的效果等级（取最高级） */
function getEffectLevel(packageId: string, store: TagStore): EffectLevel {
  const assignedIds = store.assignments[packageId] || [];
  const levelTags = assignedIds
    .map((id) => store.tags.find((t) => t.id === id))
    .filter((t): t is CustomTag => !!t && t.category === "level");
  for (const lvl of LEVEL_ORDER) {
    if (lvl === "unrated") continue;
    if (levelTags.some((t) => levelFromTagName(t.name) === lvl)) return lvl;
  }
  return "unrated";
}

/** 收集媒体包的全部素材（用于详情预览） */
function getAllAssets(pkg: MediaPackage): { label: string; asset: MediaAsset }[] {
  const items: { label: string; asset: MediaAsset }[] = [];
  if (pkg.cover) items.push({ label: "文章封面", asset: pkg.cover });
  if (pkg.wechatCover) items.push({ label: "公众号配图", asset: pkg.wechatCover });
  if (pkg.xiaohongshuGrid) {
    pkg.xiaohongshuGrid.forEach((a, i) => items.push({ label: `小红书九宫格 ${i + 1}`, asset: a }));
  }
  if (pkg.douyinCover) items.push({ label: "抖音封面", asset: pkg.douyinCover });
  if (pkg.video) items.push({ label: "短视频", asset: pkg.video });
  if (pkg.promoVideo) items.push({ label: "宣传视频", asset: pkg.promoVideo });
  if (pkg.bgm) items.push({ label: "背景音乐", asset: pkg.bgm });
  if (pkg.jingle) items.push({ label: "品牌短曲", asset: pkg.jingle });
  if (pkg.articleScript) items.push({ label: "公众号文章脚本", asset: pkg.articleScript });
  if (pkg.videoScript) items.push({ label: "视频分镜脚本", asset: pkg.videoScript });
  if (pkg.xiaohongshuScript) items.push({ label: "小红书脚本", asset: pkg.xiaohongshuScript });
  if (pkg.bilibiliScript) items.push({ label: "B站脚本", asset: pkg.bilibiliScript });
  if (pkg.douyinScript) items.push({ label: "抖音脚本", asset: pkg.douyinScript });
  if (pkg.liveStreamScript) items.push({ label: "直播脚本", asset: pkg.liveStreamScript });
  return items;
}

// ============================================
// localStorage 读写
// ============================================

/** 加载标签库（含定义与绑定），首次自动种子化效果等级标签 */
function loadTagStore(): TagStore {
  try {
    const raw = localStorage.getItem(TAGS_STORAGE_KEY);
    if (!raw) {
      return { tags: [...DEFAULT_LEVEL_TAGS], assignments: {} };
    }
    const parsed = JSON.parse(raw) as Partial<TagStore>;
    const tags = Array.isArray(parsed.tags) ? (parsed.tags as CustomTag[]) : [];
    const assignments = parsed.assignments && typeof parsed.assignments === "object" ? parsed.assignments : {};
    // 确保内置等级标签存在
    const hasLevelTags = tags.some((t) => t.category === "level");
    const finalTags = hasLevelTags ? tags : [...tags, ...DEFAULT_LEVEL_TAGS];
    return { tags: finalTags, assignments };
  } catch {
    return { tags: [...DEFAULT_LEVEL_TAGS], assignments: {} };
  }
}

/** 写入标签库 */
function saveTagStore(store: TagStore): void {
  try {
    localStorage.setItem(TAGS_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

/** 加载收藏/复用记录 */
function loadFavorites(): Record<string, FavoriteRecord> {
  try {
    const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, FavoriteRecord>) : {};
  } catch {
    return {};
  }
}

/** 写入收藏/复用记录 */
function saveFavorites(map: Record<string, FavoriteRecord>): void {
  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // 忽略
  }
}

/** 加载指定媒体包的版本记录 */
function loadVersions(packageId: string): VersionRecord[] {
  try {
    const raw = localStorage.getItem(`${VERSIONS_STORAGE_PREFIX}${packageId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as VersionRecord[]) : [];
  } catch {
    return [];
  }
}

/** 写入指定媒体包的版本记录 */
function saveVersions(packageId: string, records: VersionRecord[]): void {
  try {
    localStorage.setItem(`${VERSIONS_STORAGE_PREFIX}${packageId}`, JSON.stringify(records));
  } catch {
    // 忽略
  }
}

// ============================================
// 主组件
// ============================================

export default function ContentAssetLibrary() {
  const [packages, setPackages] = useState<MediaPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 标签库
  const [tagStore, setTagStore] = useState<TagStore>(() => loadTagStore());
  // 收藏/复用
  const [favMap, setFavMap] = useState<Record<string, FavoriteRecord>>(() => loadFavorites());

  // 检索条件
  const [search, setSearch] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [platformFilter, setPlatformFilter] = useState<string | "all">("all");
  const [levelFilter, setLevelFilter] = useState<EffectLevel | "all">("all");
  const [sortKey, setSortKey] = useState<SortKey>("newest");

  // 详情 Modal
  const [detailPkg, setDetailPkg] = useState<MediaPackage | null>(null);
  const [detailTab, setDetailTab] = useState<"preview" | "tags" | "versions">("preview");
  const [versions, setVersions] = useState<VersionRecord[]>([]);
  const [versionNote, setVersionNote] = useState("");

  // 标签管理新建表单
  const [newTagName, setNewTagName] = useState("");
  const [newTagCategory, setNewTagCategory] = useState<TagCategory>("theme");
  const [newTagColor, setNewTagColor] = useState(TAG_COLOR_PALETTE[0]);

  // 临时提示
  const [toast, setToast] = useState<{ text: string; ok: boolean } | null>(null);

  /** 显示临时提示（2.5 秒后自动消失） */
  const showToast = useCallback((text: string, ok = true) => {
    setToast({ text, ok });
    window.setTimeout(() => setToast(null), 2500);
  }, []);

  /** 加载真实媒体包列表 */
  const loadPackages = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setError("RuntimeManager 尚未初始化");
        setLoading(false);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.MediaStudio, { type: "list_packages" });
      if (resp?.success && resp.data) {
        // v5.2.3 关键修复：RuntimeManager.query 返回 { success, data: MediaStudioResult }，
        // MediaStudioResult.data 才是 { packages }。直接取 resp.data.packages 拿到 undefined → 资产库永远为空。
        const wrapped = resp.data as any;
        const inner = (wrapped && typeof wrapped === "object" && wrapped.data && Array.isArray(wrapped.data.packages))
          ? wrapped.data
          : wrapped;
        const list = Array.isArray(inner?.packages) ? (inner.packages as MediaPackage[]) : [];
        // 无条件合并 Vault 兜底（补齐前端直写/对比生成/脚本落库的新包，与营销中心产物区一致）
        try {
          const vaultPkgs = await fetchPackagesFromVault();
          const byId = new Map<string, MediaPackage>();
          for (const p of vaultPkgs) byId.set(p.id, p);
          for (const p of list) byId.set(p.id, p); // Runtime 包优先
          const merged = Array.from(byId.values()).sort((a, b) =>
            (b.createdAt || '').localeCompare(a.createdAt || '')
          );
          setPackages(merged);
          if (list.length === 0 && vaultPkgs.length > 0) setError(null);
        } catch {
          setPackages(list);
        }
      } else {
        setError(resp?.error || "查询媒体包列表失败");
      }
    } catch (err) {
      setError((err as Error).message || "加载媒体包列表异常");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // 初次挂载：加载媒体包
  useEffect(() => {
    void loadPackages();
  }, [loadPackages]);

  // 标签库变化时持久化
  useEffect(() => {
    saveTagStore(tagStore);
  }, [tagStore]);

  // 收藏变化时持久化
  useEffect(() => {
    saveFavorites(favMap);
  }, [favMap]);

  // ============================================
  // 派生数据：资产统计
  // ============================================
  const stats = useMemo(() => {
    const total = packages.length;
    const platformDist: Record<string, number> = {};
    const levelDist: Record<EffectLevel, number> = {
      viral: 0, quality: 0, normal: 0, needs_work: 0, unrated: 0,
    };
    let favoriteCount = 0;
    let reuseTotal = 0;
    let assetTotal = 0;
    for (const pkg of packages) {
      for (const p of detectPlatforms(pkg)) {
        platformDist[p] = (platformDist[p] || 0) + 1;
      }
      const lvl = getEffectLevel(pkg.id, tagStore);
      levelDist[lvl]++;
      if (favMap[pkg.id]) {
        favoriteCount++;
        reuseTotal += favMap[pkg.id].reuseCount;
      }
      assetTotal += countAssets(pkg);
    }
    return { total, platformDist, levelDist, favoriteCount, reuseTotal, assetTotal };
  }, [packages, tagStore, favMap]);

  // ============================================
  // 检索与排序
  // ============================================
  const filteredPackages = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    let list = packages.filter((pkg) => {
      // 关键词搜索：匹配 topic / description
      if (keyword) {
        const hay = `${pkg.topic || ""} ${pkg.description || ""}`.toLowerCase();
        if (!hay.includes(keyword)) return false;
      }
      // 标签筛选（多选，AND 逻辑：必须包含全部已选标签）
      if (selectedTagIds.length > 0) {
        const assigned = tagStore.assignments[pkg.id] || [];
        if (!selectedTagIds.every((id) => assigned.includes(id))) return false;
      }
      // 平台筛选
      if (platformFilter !== "all") {
        if (!detectPlatforms(pkg).includes(platformFilter)) return false;
      }
      // 效果等级筛选
      if (levelFilter !== "all") {
        if (getEffectLevel(pkg.id, tagStore) !== levelFilter) return false;
      }
      return true;
    });

    // 排序
    list = [...list];
    switch (sortKey) {
      case "newest":
        list.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
        break;
      case "oldest":
        list.sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""));
        break;
      case "favorite_first":
        list.sort((a, b) => {
          const fa = favMap[a.id] ? 1 : 0;
          const fb = favMap[b.id] ? 1 : 0;
          if (fb !== fa) return fb - fa;
          return (b.createdAt || "").localeCompare(a.createdAt || "");
        });
        break;
      case "reuse_first":
        list.sort((a, b) => (favMap[b.id]?.reuseCount || 0) - (favMap[a.id]?.reuseCount || 0));
        break;
      case "level":
        list.sort((a, b) => {
          const la = LEVEL_ORDER.indexOf(getEffectLevel(a.id, tagStore));
          const lb = LEVEL_ORDER.indexOf(getEffectLevel(b.id, tagStore));
          if (la !== lb) return la - lb;
          return (b.createdAt || "").localeCompare(a.createdAt || "");
        });
        break;
    }
    return list;
  }, [packages, search, selectedTagIds, platformFilter, levelFilter, sortKey, tagStore, favMap]);

  // ============================================
  // 操作处理
  // ============================================

  /** 切换收藏状态 */
  const toggleFavorite = useCallback((packageId: string) => {
    setFavMap((prev) => {
      const next = { ...prev };
      if (next[packageId]) {
        delete next[packageId];
      } else {
        next[packageId] = {
          packageId,
          favoritedAt: new Date().toISOString(),
          reuseCount: 0,
        };
      }
      return next;
    });
  }, []);

  /** 复用素材：复制模板摘要到剪贴板 + 增加复用计数 */
  const reusePackage = useCallback(async (pkg: MediaPackage) => {
    const platforms = detectPlatforms(pkg);
    const excerpt = getScriptExcerpt(pkg);
    const template = [
      `【内容模板复用】`,
      `主题：${pkg.topic}`,
      `平台：${platforms.map((p) => PLATFORM_LABEL[p]).join(" / ") || "未指定"}`,
      excerpt ? `脚本摘要：${excerpt}` : "",
      `来源资产 ID：${pkg.id}`,
      `创建时间：${pkg.createdAt}`,
    ].filter(Boolean).join("\n");
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(template);
      }
    } catch {
      // 剪贴板不可用时忽略，仍计入复用次数
    }
    setFavMap((prev) => {
      const existing = prev[pkg.id] || {
        packageId: pkg.id,
        favoritedAt: new Date().toISOString(),
        reuseCount: 0,
      };
      return {
        ...prev,
        [pkg.id]: {
          ...existing,
          reuseCount: existing.reuseCount + 1,
          lastReuseAt: new Date().toISOString(),
        },
      };
    });
    showToast("已复制模板摘要到剪贴板，复用次数 +1", true);
  }, [showToast]);

  /** 打开详情 Modal */
  const openDetail = useCallback((pkg: MediaPackage) => {
    setDetailPkg(pkg);
    setDetailTab("preview");
    setVersions(loadVersions(pkg.id));
    setVersionNote("");
  }, []);

  /** 关闭详情 Modal */
  const closeDetail = useCallback(() => {
    setDetailPkg(null);
    setVersions([]);
    setVersionNote("");
  }, []);

  /** 为媒体包创建版本快照 */
  const createVersion = useCallback(() => {
    if (!detailPkg) return;
    const platforms = detectPlatforms(detailPkg);
    const record: VersionRecord = {
      id: genId("ver"),
      packageId: detailPkg.id,
      createdAt: new Date().toISOString(),
      note: versionNote.trim() || "无备注",
      topic: detailPkg.topic,
      platforms,
      assetCount: countAssets(detailPkg),
      scriptExcerpt: getScriptExcerpt(detailPkg),
    };
    const next = [record, ...versions];
    setVersions(next);
    saveVersions(detailPkg.id, next);
    setVersionNote("");
    showToast("版本快照已保存", true);
  }, [detailPkg, versions, versionNote, showToast]);

  /** 删除版本记录 */
  const deleteVersion = useCallback((versionId: string) => {
    if (!detailPkg) return;
    const next = versions.filter((v) => v.id !== versionId);
    setVersions(next);
    saveVersions(detailPkg.id, next);
  }, [detailPkg, versions]);

  /** 切换媒体包的标签绑定 */
  const toggleTagOnPackage = useCallback((packageId: string, tagId: string) => {
    setTagStore((prev) => {
      const assigned = prev.assignments[packageId] || [];
      const has = assigned.includes(tagId);
      return {
        ...prev,
        assignments: {
          ...prev.assignments,
          [packageId]: has ? assigned.filter((id) => id !== tagId) : [...assigned, tagId],
        },
      };
    });
  }, []);

  /** 切换检索区的标签多选 */
  const toggleFilterTag = useCallback((tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  }, []);

  /** 新建自定义标签 */
  const createTag = useCallback(() => {
    const name = newTagName.trim();
    if (!name) return;
    setTagStore((prev) => ({
      ...prev,
      tags: [...prev.tags, { id: genId("tag"), name, category: newTagCategory, color: newTagColor }],
    }));
    setNewTagName("");
    showToast(`标签「${name}」已创建`, true);
  }, [newTagName, newTagCategory, newTagColor, showToast]);

  /** 删除自定义标签（同时清理绑定） */
  const deleteTag = useCallback((tagId: string) => {
    setTagStore((prev) => {
      const tags = prev.tags.filter((t) => t.id !== tagId);
      const assignments: Record<string, string[]> = {};
      for (const [pid, ids] of Object.entries(prev.assignments)) {
        const filtered = ids.filter((id) => id !== tagId);
        if (filtered.length > 0) assignments[pid] = filtered;
      }
      return { tags, assignments };
    });
  }, []);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-amber-500/15 border border-amber-500/30">
            <FolderArchive size={13} className="text-amber-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">内容资产库</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Content Asset Library · {stats.total} 资产 · {stats.favoriteCount} 收藏 · {stats.reuseTotal} 复用
            </p>
          </div>
        </div>
        <button
          onClick={loadPackages}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
        >
          {refreshing ? (
            <Loader2 size={11} className="text-amber-400 animate-spin" />
          ) : (
            <RefreshCw size={11} className="text-amber-400" />
          )}
          <span className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">
            {refreshing ? "刷新中..." : "刷新"}
          </span>
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className="px-3 py-2 rounded-md border border-red-500/30 bg-red-500/[0.06] text-red-300 text-[10px] font-mono">
            {error}
          </div>
        </div>
      )}

      {/* 临时提示 */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 border-b border-white/[0.04] overflow-hidden"
          >
            <div
              className={`px-3 py-2 rounded-md border text-[10px] font-mono ${
                toast.ok
                  ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                  : "border-red-500/30 bg-red-500/[0.06] text-red-300"
              }`}
            >
              {toast.text}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 size={20} className="mx-auto text-amber-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-2">加载内容资产...</p>
        </div>
      ) : packages.length === 0 ? (
        <div className="py-10 text-center">
          <FolderArchive size={22} className="mx-auto text-gray-600 mb-2" />
          <p className="text-[11px] font-mono text-gray-500">
            暂无内容资产，请先在生成工作室创建内容
          </p>
        </div>
      ) : (
        <>
          {/* 资产统计区 */}
          <div className="p-4 border-b border-white/[0.04] space-y-3">
            {/* KPI 卡片 */}
            <div className="grid grid-cols-4 gap-2">
              <KpiCard icon={FolderArchive} label="资产总量" value={String(stats.total)} color="#FBBF24" />
              <KpiCard icon={Layers} label="素材总数" value={String(stats.assetTotal)} color="#38BDF8" />
              <KpiCard icon={Star} label="收藏数" value={String(stats.favoriteCount)} color="#A78BFA" />
              <KpiCard icon={Copy} label="复用次数" value={String(stats.reuseTotal)} color="#34D399" />
            </div>

            {/* 平台分布 + 质量分布 */}
            <div className="grid grid-cols-2 gap-3">
              <DistPanel
                title="各平台分布"
                icon={<BarChart3 size={10} className="text-sky-400" />}
                bars={Object.entries(PLATFORM_LABEL).map(([key, label]) => ({
                  label,
                  value: stats.platformDist[key] || 0,
                  color: PLATFORM_COLOR[key],
                }))}
              />
              <DistPanel
                title="质量分布"
                icon={<BarChart3 size={10} className="text-emerald-400" />}
                bars={LEVEL_ORDER.map((lvl) => ({
                  label: LEVEL_LABEL[lvl],
                  value: stats.levelDist[lvl],
                  color: LEVEL_COLOR[lvl],
                }))}
              />
            </div>
          </div>

          {/* 检索区 */}
          <div className="p-4 border-b border-white/[0.04] space-y-2.5">
            {/* 搜索框 + 排序 */}
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06]">
                <Search size={11} className="text-gray-500 flex-shrink-0" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="搜索主题或描述..."
                  className="flex-1 bg-transparent text-[11px] text-gray-200 font-mono focus:outline-none placeholder:text-gray-600"
                />
                {search && (
                  <button onClick={() => setSearch("")} className="text-gray-600 hover:text-gray-300">
                    <X size={10} />
                  </button>
                )}
              </div>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] font-mono text-gray-300 focus:outline-none focus:border-amber-500/40"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* 平台筛选 + 效果等级筛选 */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1">
                <Filter size={10} className="text-gray-500" />
                <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mr-1">平台：</span>
                <FilterChip
                  active={platformFilter === "all"}
                  onClick={() => setPlatformFilter("all")}
                  label="全部"
                />
                {Object.entries(PLATFORM_LABEL).map(([key, label]) => (
                  <FilterChip
                    key={key}
                    active={platformFilter === key}
                    onClick={() => setPlatformFilter(key)}
                    label={label}
                    color={PLATFORM_COLOR[key]}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mr-1">效果等级：</span>
              <FilterChip
                active={levelFilter === "all"}
                onClick={() => setLevelFilter("all")}
                label="全部"
              />
              {LEVEL_ORDER.map((lvl) => (
                <FilterChip
                  key={lvl}
                  active={levelFilter === lvl}
                  onClick={() => setLevelFilter(lvl)}
                  label={LEVEL_LABEL[lvl]}
                  color={LEVEL_COLOR[lvl]}
                />
              ))}
            </div>

            {/* 标签多选筛选 */}
            {tagStore.tags.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                <Tag size={10} className="text-gray-500" />
                <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mr-1">标签：</span>
                {selectedTagIds.length > 0 && (
                  <button
                    onClick={() => setSelectedTagIds([])}
                    className="px-1.5 py-0.5 rounded text-[9px] font-mono text-gray-500 hover:text-red-400 transition-colors"
                  >
                    清除
                  </button>
                )}
                {tagStore.tags.map((tag) => (
                  <FilterChip
                    key={tag.id}
                    active={selectedTagIds.includes(tag.id)}
                    onClick={() => toggleFilterTag(tag.id)}
                    label={tag.name}
                    color={tag.color}
                  />
                ))}
              </div>
            )}
          </div>

          {/* 资产卡片网格 */}
          <div className="p-4">
            {filteredPackages.length === 0 ? (
              <div className="py-8 text-center">
                <Search size={22} className="mx-auto text-gray-600 mb-2" />
                <p className="text-[11px] font-mono text-gray-500">
                  {packages.length === 0 ? "暂无内容资产" : "当前检索条件下无匹配资产"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredPackages.map((pkg) => (
                  <AssetCard
                    key={pkg.id}
                    pkg={pkg}
                    tagStore={tagStore}
                    fav={favMap[pkg.id]}
                    onToggleFav={toggleFavorite}
                    onReuse={reusePackage}
                    onOpenDetail={openDetail}
                  />
                ))}
              </div>
            )}
          </div>

          {/* 标签管理区 */}
          <div className="p-4 border-t border-white/[0.04]">
            <div className="flex items-center gap-2 mb-2.5">
              <Tag size={11} className="text-amber-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">标签管理</span>
            </div>
            <div className="space-y-2">
              {(["theme", "style", "platform", "level"] as TagCategory[]).map((cat) => {
                const catTags = tagStore.tags.filter((t) => t.category === cat);
                return (
                  <div key={cat} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: TAG_CATEGORY_COLOR[cat] }}
                      />
                      <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
                        {TAG_CATEGORY_LABEL[cat]}（{catTags.length}）
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {catTags.length === 0 ? (
                        <span className="text-[10px] font-mono text-gray-600">暂无标签</span>
                      ) : (
                        catTags.map((tag) => (
                          <span
                            key={tag.id}
                            className="group flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono"
                            style={{ background: `${tag.color}20`, color: tag.color }}
                          >
                            {tag.name}
                            <button
                              onClick={() => deleteTag(tag.id)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-400"
                            >
                              <X size={9} />
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 新建标签表单 */}
            <div className="mt-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Plus size={10} className="text-amber-400" />
                <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">新建标签</span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <input
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") createTag(); }}
                  placeholder="标签名称"
                  className="flex-1 min-w-[100px] px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-amber-500/40"
                />
                <select
                  value={newTagCategory}
                  onChange={(e) => setNewTagCategory(e.target.value as TagCategory)}
                  className="px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] font-mono text-gray-300 focus:outline-none focus:border-amber-500/40"
                >
                  {(["theme", "style", "platform", "level"] as TagCategory[]).map((c) => (
                    <option key={c} value={c}>{TAG_CATEGORY_LABEL[c]}</option>
                  ))}
                </select>
                <div className="flex items-center gap-0.5">
                  {TAG_COLOR_PALETTE.map((c) => (
                    <button
                      key={c}
                      onClick={() => setNewTagColor(c)}
                      className={`w-4 h-4 rounded ${newTagColor === c ? "ring-1 ring-white/60" : ""}`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
                <button
                  onClick={createTag}
                  disabled={!newTagName.trim()}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus size={10} />
                  添加
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 资产详情 Modal */}
      <AnimatePresence>
        {detailPkg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={closeDetail}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl max-h-[85vh] rounded-lg border border-white/[0.06] bg-[#0e1320]/95 backdrop-blur-md overflow-hidden flex flex-col"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 min-w-0">
                  <FolderArchive size={14} className="text-amber-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <h4 className="text-[13px] font-semibold text-white truncate">{detailPkg.topic}</h4>
                    <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
                      ID: {detailPkg.id} · {detailPkg.createdAt} · {countAssets(detailPkg)} 素材
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeDetail}
                  className="p-1 rounded text-gray-500 hover:text-gray-200 hover:bg-white/[0.04]"
                >
                  <X size={14} />
                </button>
              </div>

              {/* Modal Tabs */}
              <div className="flex items-center gap-1 px-4 py-2 border-b border-white/[0.06]">
                {([
                  { key: "preview", label: "内容预览", icon: FileText },
                  { key: "tags", label: "标签编辑", icon: Tag },
                  { key: "versions", label: "版本历史", icon: History },
                ] as const).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setDetailTab(tab.key)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-mono uppercase tracking-wider transition-colors ${
                      detailTab === tab.key
                        ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                        : "text-gray-500 hover:text-gray-300 border border-transparent"
                    }`}
                  >
                    <tab.icon size={10} />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-4">
                {detailTab === "preview" && <DetailPreview pkg={detailPkg} />}
                {detailTab === "tags" && (
                  <DetailTagEditor
                    pkg={detailPkg}
                    tagStore={tagStore}
                    onToggle={(tagId) => toggleTagOnPackage(detailPkg.id, tagId)}
                  />
                )}
                {detailTab === "versions" && (
                  <DetailVersionHistory
                    versions={versions}
                    note={versionNote}
                    onNoteChange={setVersionNote}
                    onCreate={createVersion}
                    onDelete={deleteVersion}
                  />
                )}
              </div>

              {/* Modal Footer：复用操作 */}
              <div className="flex items-center justify-between p-3 border-t border-white/[0.06]">
                <span className="text-[9px] font-mono text-gray-600">
                  平台：{detectPlatforms(detailPkg).map((p) => PLATFORM_LABEL[p]).join(" / ") || "未指定"}
                </span>
                <button
                  onClick={() => void reusePackage(detailPkg)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
                >
                  <Copy size={11} />
                  <span className="text-[10px] font-mono uppercase tracking-wider">一键复用模板</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// KPI 小卡片
// ============================================
interface KpiCardProps {
  icon: typeof FolderArchive;
  label: string;
  value: string;
  color: string;
}

function KpiCard({ icon: Icon, label, value, color }: KpiCardProps) {
  return (
    <div
      className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={13} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">{label}</div>
        <div className="text-[13px] font-mono font-semibold truncate" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 分布面板（平台分布 / 质量分布）
// ============================================
interface DistPanelProps {
  title: string;
  icon: React.ReactNode;
  bars: { label: string; value: number; color: string }[];
}

function DistPanel({ title, icon, bars }: DistPanelProps) {
  const max = Math.max(...bars.map((b) => b.value), 1);
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">{title}</span>
      </div>
      <div className="space-y-1">
        {bars.map((b) => {
          const widthPct = b.value === 0 ? 0 : Math.max(10, Math.round((b.value / max) * 100));
          return (
            <div key={b.label} className="flex items-center gap-2">
              <span className="w-12 text-[10px] font-mono text-gray-300 truncate">{b.label}</span>
              <div className="flex-1 h-3 rounded bg-white/[0.02] overflow-hidden">
                <div
                  className="h-full rounded transition-all"
                  style={{ width: `${widthPct}%`, background: `linear-gradient(90deg, ${b.color}80, ${b.color})` }}
                />
              </div>
              <span className="text-[9px] font-mono text-gray-400 w-5 text-right">{b.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// 筛选 Chip
// ============================================
interface FilterChipProps {
  active: boolean;
  onClick: () => void;
  label: string;
  color?: string;
}

function FilterChip({ active, onClick, label, color }: FilterChipProps) {
  return (
    <button
      onClick={onClick}
      className={`px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors border ${
        active
          ? "text-white"
          : "text-gray-500 hover:text-gray-300 border-transparent"
      }`}
      style={active ? {
        background: `${color || "#FBBF24"}20`,
        color: color || "#FBBF24",
        borderColor: `${color || "#FBBF24"}40`,
      } : undefined}
    >
      {label}
    </button>
  );
}

// ============================================
// 资产卡片
// ============================================
interface AssetCardProps {
  pkg: MediaPackage;
  tagStore: TagStore;
  fav?: FavoriteRecord;
  onToggleFav: (id: string) => void;
  onReuse: (pkg: MediaPackage) => void;
  onOpenDetail: (pkg: MediaPackage) => void;
}

function AssetCard({ pkg, tagStore, fav, onToggleFav, onReuse, onOpenDetail }: AssetCardProps) {
  const cover = getCoverAsset(pkg);
  const coverUrl = assetImageUrl(cover);
  const platforms = detectPlatforms(pkg);
  const level = getEffectLevel(pkg.id, tagStore);
  const assignedTags = (tagStore.assignments[pkg.id] || [])
    .map((id) => tagStore.tags.find((t) => t.id === id))
    .filter((t): t is CustomTag => !!t);
  const isFav = !!fav;
  const assetCount = countAssets(pkg);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden flex flex-col"
    >
      {/* 封面缩略 */}
      <div
        className="relative h-24 bg-white/[0.02] cursor-pointer overflow-hidden"
        onClick={() => onOpenDetail(pkg)}
      >
        {coverUrl ? (
          <img src={coverUrl} alt={pkg.topic} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon size={20} className="text-gray-700" />
          </div>
        )}
        {/* 平台图标叠加 */}
        <div className="absolute top-1 left-1 flex items-center gap-0.5">
          {platforms.map((p) => (
            <span
              key={p}
              className="px-1 py-0.5 rounded text-[8px] font-mono"
              style={{ background: `${PLATFORM_COLOR[p]}30`, color: PLATFORM_COLOR[p] }}
            >
              {PLATFORM_LABEL[p]}
            </span>
          ))}
        </div>
        {/* 效果等级徽章 */}
        <div className="absolute top-1 right-1">
          <span
            className="px-1 py-0.5 rounded text-[8px] font-mono"
            style={{ background: `${LEVEL_COLOR[level]}30`, color: LEVEL_COLOR[level] }}
          >
            {LEVEL_LABEL[level]}
          </span>
        </div>
        {/* 收藏按钮 */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleFav(pkg.id); }}
          className="absolute bottom-1 right-1 p-1 rounded bg-black/40 hover:bg-black/60 transition-colors"
        >
          <Star
            size={11}
            className={isFav ? "text-amber-400" : "text-gray-500"}
            fill={isFav ? "#FBBF24" : "none"}
          />
        </button>
      </div>

      {/* 主体信息 */}
      <div className="p-2.5 flex-1 flex flex-col">
        <div
          className="text-[11px] text-gray-200 font-medium line-clamp-2 cursor-pointer hover:text-amber-300 transition-colors"
          onClick={() => onOpenDetail(pkg)}
        >
          {pkg.topic}
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-[9px] font-mono text-gray-600">
          <Calendar size={8} />
          <span>{(pkg.createdAt || "").slice(0, 10)}</span>
          <span>·</span>
          <Layers size={8} />
          <span>{assetCount} 素材</span>
          {fav && fav.reuseCount > 0 && (
            <>
              <span>·</span>
              <Copy size={8} />
              <span className="text-emerald-400">{fav.reuseCount}</span>
            </>
          )}
        </div>

        {/* 标签 */}
        {assignedTags.length > 0 && (
          <div className="flex items-center gap-0.5 flex-wrap mt-1.5">
            {assignedTags.slice(0, 4).map((tag) => (
              <span
                key={tag.id}
                className="px-1 rounded text-[8px] font-mono"
                style={{ background: `${tag.color}20`, color: tag.color }}
              >
                {tag.name}
              </span>
            ))}
            {assignedTags.length > 4 && (
              <span className="text-[8px] font-mono text-gray-600">+{assignedTags.length - 4}</span>
            )}
          </div>
        )}

        {/* 状态 */}
        <div className="flex items-center gap-1 mt-1.5">
          <span
            className={`px-1 py-0.5 rounded text-[8px] font-mono ${
              pkg.status === "completed"
                ? "bg-emerald-500/15 text-emerald-400"
                : pkg.status === "generating"
                ? "bg-amber-500/15 text-amber-400"
                : pkg.status === "partial"
                ? "bg-sky-500/15 text-sky-400"
                : pkg.status === "failed"
                ? "bg-red-500/15 text-red-400"
                : "bg-gray-500/15 text-gray-400"
            }`}
          >
            {pkg.status}
          </span>
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="flex items-center gap-1 px-2.5 pb-2.5">
        <button
          onClick={() => onOpenDetail(pkg)}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-white/[0.04] border border-white/[0.06] text-gray-300 hover:bg-white/[0.08] transition-colors"
        >
          <Edit3 size={10} />
          详情
        </button>
        <button
          onClick={() => void onReuse(pkg)}
          className="flex items-center justify-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
          title="复用模板"
        >
          <Copy size={10} />
        </button>
        <button
          onClick={() => onOpenDetail(pkg)}
          className="flex items-center justify-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-sky-500/15 border border-sky-500/30 text-sky-300 hover:bg-sky-500/25 transition-colors"
          title="版本历史"
        >
          <History size={10} />
        </button>
      </div>
    </motion.div>
  );
}

// ============================================
// 详情：内容预览
// ============================================
function DetailPreview({ pkg }: { pkg: MediaPackage }) {
  const assets = getAllAssets(pkg);
  const platforms = detectPlatforms(pkg);

  return (
    <div className="space-y-3">
      {/* 基本信息 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">主题</div>
        <div className="text-[12px] text-gray-200">{pkg.topic}</div>
        {pkg.description && (
          <div className="text-[10px] font-mono text-gray-500 mt-1.5 leading-relaxed">{pkg.description}</div>
        )}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {platforms.map((p) => (
            <span
              key={p}
              className="px-1.5 py-0.5 rounded text-[9px] font-mono"
              style={{ background: `${PLATFORM_COLOR[p]}20`, color: PLATFORM_COLOR[p] }}
            >
              {PLATFORM_LABEL[p]}
            </span>
          ))}
        </div>
      </div>

      {/* 素材列表 */}
      {assets.length === 0 ? (
        <div className="py-6 text-center">
          <FileText size={20} className="mx-auto text-gray-600 mb-1.5" />
          <p className="text-[10px] font-mono text-gray-600">该资产包暂无素材</p>
        </div>
      ) : (
        <div className="space-y-2">
          {assets.map((item, idx) => (
            <AssetPreviewItem key={`${item.asset.id || idx}`} label={item.label} asset={item.asset} />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// 详情：单个素材预览
// ============================================
function AssetPreviewItem({ label, asset }: { label: string; asset: MediaAsset }) {
  const imgUrl = assetImageUrl(asset);
  const isScript = asset.type === "script" || !!asset.content;

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
      <div className="flex items-center gap-1.5 mb-1.5">
        {asset.type === "image" && <ImageIcon size={10} className="text-sky-400" />}
        {asset.type === "video" && <VideoIcon size={10} className="text-pink-400" />}
        {asset.type === "audio" && <Music size={10} className="text-emerald-400" />}
        {asset.type === "script" && <FileText size={10} className="text-amber-400" />}
        <span className="text-[10px] font-mono text-gray-300">{label}</span>
        <span className="text-[8px] font-mono text-gray-600 ml-auto">
          {asset.model || asset.type}
        </span>
      </div>
      {imgUrl && (
        <div className="mb-1.5 rounded-md overflow-hidden border border-white/[0.04]">
          <img src={imgUrl} alt={label} className="w-full max-h-48 object-contain bg-black/20" />
        </div>
      )}
      {asset.url && !imgUrl && (
        <div className="mb-1.5 px-2 py-1 rounded-md bg-white/[0.03] text-[10px] font-mono text-sky-400 truncate">
          {asset.url}
        </div>
      )}
      {isScript && asset.content && (
        <pre className="px-2 py-1.5 rounded-md bg-black/20 text-[10px] font-mono text-gray-300 whitespace-pre-wrap max-h-40 overflow-y-auto leading-relaxed">
          {asset.content}
        </pre>
      )}
      {!imgUrl && !asset.url && !asset.content && (
        <p className="text-[10px] font-mono text-gray-600">无可用预览内容</p>
      )}
    </div>
  );
}

// ============================================
// 详情：标签编辑
// ============================================
interface DetailTagEditorProps {
  pkg: MediaPackage;
  tagStore: TagStore;
  onToggle: (tagId: string) => void;
}

function DetailTagEditor({ pkg, tagStore, onToggle }: DetailTagEditorProps) {
  const assignedIds = new Set(tagStore.assignments[pkg.id] || []);

  return (
    <div className="space-y-2.5">
      {(["theme", "style", "platform", "level"] as TagCategory[]).map((cat) => {
        const catTags = tagStore.tags.filter((t) => t.category === cat);
        return (
          <div key={cat} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
            <div className="flex items-center gap-1.5 mb-1.5">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: TAG_CATEGORY_COLOR[cat] }}
              />
              <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
                {TAG_CATEGORY_LABEL[cat]}
              </span>
            </div>
            {catTags.length === 0 ? (
              <p className="text-[10px] font-mono text-gray-600">暂无该分类标签，请在下方标签管理区创建</p>
            ) : (
              <div className="flex items-center gap-1 flex-wrap">
                {catTags.map((tag) => {
                  const active = assignedIds.has(tag.id);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => onToggle(tag.id)}
                      className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono border transition-colors ${
                        active ? "" : "border-transparent text-gray-500 hover:text-gray-300"
                      }`}
                      style={active ? {
                        background: `${tag.color}20`,
                        color: tag.color,
                        borderColor: `${tag.color}40`,
                      } : undefined}
                    >
                      {active && <CheckCircle2 size={9} />}
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// 详情：版本历史
// ============================================
interface DetailVersionHistoryProps {
  versions: VersionRecord[];
  note: string;
  onNoteChange: (v: string) => void;
  onCreate: () => void;
  onDelete: (id: string) => void;
}

function DetailVersionHistory({ versions, note, onNoteChange, onCreate, onDelete }: DetailVersionHistoryProps) {
  return (
    <div className="space-y-2.5">
      {/* 创建版本快照 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Save size={10} className="text-amber-400" />
          <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">创建版本快照</span>
        </div>
        <div className="flex items-center gap-1.5">
          <input
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onCreate(); }}
            placeholder="版本备注（如：调整脚本结构）"
            className="flex-1 px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-amber-500/40"
          />
          <button
            onClick={onCreate}
            className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 transition-colors"
          >
            <Save size={10} />
            保存
          </button>
        </div>
      </div>

      {/* 版本列表 */}
      {versions.length === 0 ? (
        <div className="py-6 text-center">
          <History size={20} className="mx-auto text-gray-600 mb-1.5" />
          <p className="text-[10px] font-mono text-gray-600">暂无版本记录</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {versions.map((v) => (
            <div key={v.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <History size={10} className="text-sky-400" />
                  <span className="text-[10px] font-mono text-gray-300">{v.note}</span>
                </div>
                <button
                  onClick={() => onDelete(v.id)}
                  className="text-gray-600 hover:text-red-400 transition-colors"
                >
                  <Trash2 size={10} />
                </button>
              </div>
              <div className="text-[9px] font-mono text-gray-600 mt-1">
                {v.createdAt} · {v.assetCount} 素材 · {v.platforms.map((p) => PLATFORM_LABEL[p] || p).join("/") || "无平台"}
              </div>
              <div className="text-[9px] font-mono text-gray-500 mt-0.5">主题：{v.topic}</div>
              {v.scriptExcerpt && (
                <div className="text-[9px] font-mono text-gray-600 mt-1 leading-relaxed line-clamp-2">
                  {v.scriptExcerpt}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
